import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import TextChaos from "@/components/shared/TextChaos";
import {
  playEnterSound,
  playHoverTick,
  playPageTransition,
} from "@/components/shared/ambient-sound";
import { LANDSCAPE_LOGO_URL, SQUARE_LOGO_URL } from "@/components/shared/Logo";

const SquareLogo = () => (
  <img
    src={SQUARE_LOGO_URL}
    alt="Startup Unplugged symbol"
    className="w-full h-full object-contain bg-black p-1"
    draggable={false}
  />
);

const Wordmark = () => (
  <img
    src={LANDSCAPE_LOGO_URL}
    alt="Startup Unplugged wordmark"
    className="w-full h-full object-contain bg-black p-2"
    draggable={false}
  />
);

export default function EntryExperience() {
  // Default to "shown". We flip to "skip" if already seen.
  const [skip, setSkip] = useState(false);
  const [phase, setPhase] = useState(0);
  const [exiting, setExiting] = useState(false);

  // Decide once on mount whether to show
  useEffect(() => {
    let already = false;
    try {
      already =
        typeof window !== "undefined" &&
        (window.__startupUnpluggedIntroSeen ||
          sessionStorage.getItem(SEEN_KEY) === "1");
    } catch (_) {
      already = false;
    }

    if (already) {
      setSkip(true);
      return undefined;
    }

    // Lock body scroll while intro is active
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const timers = [
      setTimeout(() => setPhase(1), 100),
      setTimeout(() => setPhase(2), 300),
      setTimeout(() => setPhase(3), 600),
      setTimeout(() => setPhase(4), 1000),
    ];

    return () => {
      timers.forEach(clearTimeout);
      document.body.style.overflow = prev;
    };
  }, []);

  const handleEnter = () => {
    try {
      window.__startupUnpluggedIntroSeen = true;
    } catch (_) {
      /* ignore */
    }
    playEnterSound();
    // Whoosh during the fade/scale exit transition
    setTimeout(() => playPageTransition(), 80);
    setExiting(true);
    setTimeout(() => setSkip(true), 700);
  };

  if (skip) return null;

  return (
    <div
      data-testid="entry-experience"
      className={`entry-screen ${exiting ? "entry-exiting" : ""}`}
      role="dialog"
      aria-label="Startup Unplugged intro"
    >
      <div className="entry-noise grain" />

      {/* Top-left: square logo */}
      <div
        data-testid="entry-corner-tl"
        className={`entry-corner corner-top-left ${phase >= 1 ? "is-live" : ""}`}
      >
        <SquareLogo />
      </div>

      {/* Top-right: kicker text */}
      <div
        data-testid="entry-corner-tr"
        className={`entry-corner corner-top-right ${phase >= 2 ? "is-live" : ""}`}
      >
        <p>What's cooking?</p>
      </div>

      {/* Bottom-left: response text */}
      <div
        data-testid="entry-corner-bl"
        className={`entry-corner corner-bottom-left ${phase >= 3 ? "is-live" : ""}`}
      >
        <p>Good question.</p>
      </div>

      {/* Bottom-right: wordmark */}
      <div
        data-testid="entry-corner-br"
        className={`entry-corner corner-bottom-right ${phase >= 2 ? "is-live" : ""}`}
      >
        <Wordmark />
      </div>

      {/* Hairlines connecting corners */}
      <div
        className={`entry-hairline hairline-top ${phase >= 2 ? "is-live" : ""}`}
      />
      <div
        className={`entry-hairline hairline-bottom ${phase >= 3 ? "is-live" : ""}`}
      />
      <div
        className={`entry-hairline hairline-left ${phase >= 1 ? "is-live" : ""}`}
      />
      <div
        className={`entry-hairline hairline-right ${phase >= 2 ? "is-live" : ""}`}
      />

      <div className="entry-content">
        <p
          className={`entry-kicker ${phase >= 1 ? "is-live" : ""}`}
          data-testid="entry-kicker"
        >
          // Startup Unplugged · Vol. 11 · Delhi NCR
        </p>

        <TextChaos
          as="h2"
          text="What's cooking? Good question."
          className={`entry-title ${phase >= 1 ? "entry-title-live" : ""}`}
          stagger={35}
          cycles={6}
          frameStep={2}
          sound={true}
          repeat={false}
        />

        <p
          className={`entry-desc ${phase >= 3 ? "is-live" : ""}`}
          data-testid="entry-desc"
        >
          Founders, capital, startup culture, and visual chaos — all about to
          collide in one loud, internet-native launchpad for Delhi NCR.
        </p>

        <div className={`entry-meta ${phase >= 3 ? "is-live" : ""}`}>
          <span>Loading network</span>
          <span className="entry-bar">
            <span
              className="entry-bar-fill"
              style={{ width: `${Math.min(phase * 25, 100)}%` }}
            />
          </span>
          <span>{Math.min(phase * 25, 100)}%</span>
        </div>

        <div className="entry-cta-row">
          <button
            data-testid="entry-enter-btn"
            type="button"
            className={`entry-cta ${phase >= 4 ? "is-ready" : ""}`}
            disabled={phase < 4}
            onMouseEnter={() => phase >= 4 && playHoverTick()}
            onClick={handleEnter}
          >
            <span>Enter the network</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
